import dayjs from "dayjs";

import { ClientV2 } from "../../api/generated";
import { Typo } from "../../Typo";

type NewClientsTableForOverviewProps = {
  clients: ClientV2[] | [];
  translatedContent: NewClientsTableTranslatedContent;
  redactedFields?: string[];
  isMobile?: boolean;
  isBackoffice?: boolean;
};

export type NewClientsTableTranslatedContent = {
  header: string;
  clientIdColumn: string;
  nameColumn: string;
  approvalDateColumn: string;
  noDataFound: string;
};

export const NewClientsTable = ({
  clients,
  translatedContent,
  redactedFields = [],
  isMobile = false,
  isBackoffice = false,
}: NewClientsTableForOverviewProps) => {
  const DATE_FORMAT = "DD MMM YYYY";

  const formatDate = (epochMillis: number) => {
    if (epochMillis < 0) {
      return "-";
    }
    return dayjs(epochMillis).format(DATE_FORMAT).toUpperCase();
  };

  const columns: {
    field: keyof ClientV2;
    isRedacted?: boolean;
    title: string;
  }[] = [
    {
      field: "clientID",
      isRedacted: redactedFields.includes("clientId"),
      title:
        translatedContent.clientIdColumn +
        (redactedFields.includes("clientId") ? " (HIDDEN)" : ""),
    },
    {
      field: "fullName",
      isRedacted: redactedFields.includes("name"),
      title:
        translatedContent.nameColumn +
        (redactedFields.includes("name") ? " (HIDDEN)" : ""),
    },
  ];

  return (
    <div className="flex min-w-[300px] flex-col overflow-hidden rounded-2xl border border-stroke-primary bg-surface-base-background shadow-sm lg:min-w-[500px] lg:flex-1">
      <div className="flex items-center justify-between p-6">
        <Typo
          element="h1"
          type={isMobile ? "labelMD" : "labelLG"}
          textColor="text-text-primary"
        >
          {translatedContent.header}
        </Typo>
      </div>

      <div className={`${isMobile ? "overflow-x-auto" : "overflow"}`}>
        <table className="w-full">
          <thead>
            <tr className="border-b border-stroke-primary">
              {isBackoffice ? (
                <>
                  {columns.map((column) => (
                    <Typo
                      key={column.field as string}
                      element="th"
                      type="captionSM"
                      textColor="text-text-secondary"
                      classes={`pl-6 py-4 text-left ${
                        column.isRedacted ? "bg-red-100" : ""
                      }`}
                    >
                      {column.title}
                    </Typo>
                  ))}
                </>
              ) : (
                <Typo
                  element="th"
                  type="captionSM"
                  textColor="text-text-secondary"
                  classes="pl-6 py-4 text-left"
                >
                  {!redactedFields?.includes("name")
                    ? translatedContent.nameColumn
                    : translatedContent.clientIdColumn}
                </Typo>
              )}
              <Typo
                element="th"
                type="captionSM"
                textColor="text-text-secondary"
                classes="text-right pr-6 py-4"
              >
                {translatedContent.approvalDateColumn}
              </Typo>
            </tr>
          </thead>
          <tbody className="overflow-x-auto text-text-base-secondary">
            {clients.length === 0 ? (
              <tr>
                <td className="text-N px-6 py-4 text-gray-500" colSpan={3}>
                  {translatedContent.noDataFound}
                </td>
              </tr>
            ) : (
              clients.map((client, index) => (
                <tr
                  key={index}
                  className={`border-stroke-primary transition-colors hover:bg-gray-50 ${index === clients.length - 1 ? "" : "border-b"}`}
                >
                  {isBackoffice ? (
                    <>
                      {columns.map((column) => (
                        <Typo
                          key={column.field as string}
                          element="td"
                          type={isMobile ? "bodySM" : "captionMD"}
                          textColor="text-text-primary"
                          classes={`pl-6 py-5 text-left min-w-[200px] truncate ellipsis ${
                            column.isRedacted ? "bg-red-100" : ""
                          }`}
                        >
                          {client[column.field]}
                        </Typo>
                      ))}
                    </>
                  ) : (
                    <Typo
                      element="td"
                      type={isMobile ? "bodySM" : "captionMD"}
                      textColor="text-text-primary"
                      classes="pl-6 py-5 text-left min-w-[200px] truncate ellipsis"
                    >
                      {!redactedFields?.includes("name")
                        ? client.fullName
                        : client.clientID}
                    </Typo>
                  )}
                  <Typo
                    element="td"
                    type={isMobile ? "bodySM" : "captionMD"}
                    textColor="text-text-primary"
                    classes="text-right pr-6 py-5 min-w-[120px]"
                  >
                    {formatDate(client.createdAt)}
                  </Typo>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
